import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { switchMap, take, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Request } from '../../models/request.model';
import { RequestsV2ImplService } from '../../services/requests-v2.impl.service';
import { RequestPrintDialogComponent, RequestPrintDialogData } from '../request-print-dialog/request-print-dialog.component';

@Component({
    selector: 'app-request-internal-panel',
    templateUrl: './request-internal-panel.component.html',
    styleUrls: ['./request-internal-panel.component.scss'],
    providers: [DatePipe],
})
export class RequestInternalPanelComponent implements OnInit, OnDestroy {
    @Input() request: Request;
    @Input() canEdit: boolean = false;

    @Output() requestChanged = new EventEmitter<Request>();
    @Output() refreshNeeded = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();
    @Output() navigateToRequest = new EventEmitter<Request>();

    @ViewChild('descTextarea') private descTextareaRef: ElementRef<HTMLTextAreaElement>;

    tagCtrl = new FormControl('');
    filteredTags: string[] = [];
    separatorKeyCodes: number[] = [ENTER, COMMA];

    descriptionCtrl = new FormControl('');
    editingDescription = false;
    savingDescription = false;

    private tagSearch$ = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private datePipe: DatePipe,
        private translate: TranslateService,
        private requestsService: RequestsV2ImplService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.tagSearch$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    if (!term.trim()) return of([]);
                    return this.requestsService.searchTags(term);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((tags) => {
                const current = this.request?.tags || [];
                this.filteredTags = tags.filter((t) => !current.includes(t));
            });

        this.tagCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            if (value) {
                this.tagSearch$.next(value);
            } else {
                this.filteredTags = [];
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    print(): void {
        this.dialog.open<RequestPrintDialogComponent, RequestPrintDialogData>(RequestPrintDialogComponent, {
            data: { request: this.request },
            panelClass: 'no-padding-dialog',
            autoFocus: false,
        });
    }

    get slaStatus(): 'past' | 'soon' | 'future' {
        const slaDate = this.request?.sla;
        if (!slaDate) return 'future';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sla = new Date(slaDate);
        sla.setHours(0, 0, 0, 0);
        const diffDays = Math.round((sla.getTime() - today.getTime()) / 86400000);
        if (diffDays < 0) return 'past';
        if (diffDays <= 1) return 'soon';
        return 'future';
    }

    formatShortDate(date: Date | string): string {
        if (!date) return '';
        const locale = this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA';
        return this.datePipe.transform(date, 'd MMM', undefined, locale) || '';
    }

    private clearTagSuggestions(): void {
        this.filteredTags = [];
    }

    addTag(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value && !(this.request?.tags || []).includes(value)) {
            this.saveRequestTags([...(this.request?.tags || []), value]);
        }
        event.chipInput?.clear();
        this.tagCtrl.setValue(null);
    }

    removeTag(tag: string): void {
        this.saveRequestTags((this.request?.tags || []).filter((t) => t !== tag));
    }

    selectTag(event: MatAutocompleteSelectedEvent): void {
        const value = event.option.viewValue;
        if (!(this.request?.tags || []).includes(value)) {
            this.saveRequestTags([...(this.request?.tags || []), value]);
        }
        this.tagCtrl.setValue(null);
    }

    startEditDescription(): void {
        this.descriptionCtrl.setValue(this.request?.internalDescription || '');
        this.editingDescription = true;
        setTimeout(() => this.descTextareaRef?.nativeElement?.focus(), 0);
    }

    cancelEditDescription(): void {
        this.editingDescription = false;
        this.descriptionCtrl.setValue('');
    }

    saveDescription(): void {
        if (this.savingDescription) return;
        this.savingDescription = true;
        const value = this.descriptionCtrl.value ?? '';
        this.requestsService
            .updateInternalDescription(this.request._id, value)
            .pipe(take(1))
            .subscribe({
                next: (updated) => {
                    this.request = updated;
                    this.requestChanged.emit(updated);
                    this.editingDescription = false;
                    this.savingDescription = false;
                },
                error: () => {
                    this.savingDescription = false;
                },
            });
    }

    private saveRequestTags(tags: string[]): void {
        this.requestsService
            .updateRequestTags(this.request._id, tags)
            .pipe(
                switchMap(() => this.requestsService.getRequestDetails(this.request._id)),
                take(1),
            )
            .subscribe((updatedRequest) => {
                this.request = updatedRequest;
                this.requestChanged.emit(updatedRequest);
                this.clearTagSuggestions();
            });
    }
}
